import React, { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import Layout from './Layout';
import { Shield, Clock, User, Activity, FileText, Search, RefreshCw } from 'lucide-react';
import './Management.css';

const AccessLogs = () => {
    const { user } = useStore();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAction, setFilterAction] = useState('all');

    const fetchLogs = async () => {
        setIsLoading(true);
        try {
            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                ? 'http://localhost:3001/api' 
                : `${window.location.origin}/api`;
            
            const response = await fetch(`${baseUrl}/access-logs`);
            const data = await response.json();
            setLogs(data);
        } catch (error) {
            console.error('Failed to fetch logs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const sortedLogs = [...logs]
        .filter(log => {
            const matchesSearch = 
                log.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.details.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesAction = filterAction === 'all' || log.action === filterAction;
            
            return matchesSearch && matchesAction;
        })
        .sort((a, b) => {
            // 1. 아이디순 (ID 정렬)
            const idCompare = a.userId.localeCompare(b.userId);
            if (idCompare !== 0) return idCompare;
            
            // 2. 일시순 (최신순 DESC)
            return new Date(b.timestamp) - new Date(a.timestamp);
        });

    const formatTimestamp = (ts) => {
        const date = new Date(ts);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getActionBadge = (action) => {
        switch (action) {
            case 'login':
                return <span className="submit-badge" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid #10b981' }}>로그인</span>;
            case 'logout':
                return <span className="submit-badge" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444' }}>로그아웃</span>;
            case 'update':
                return <span className="submit-badge" style={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid #38bdf8' }}>데이터 수정</span>;
            default:
                return <span className="submit-badge pending">{action}</span>;
        }
    };

    if (user?.id !== 'admin') {
        return (
            <Layout>
                <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '20px' }}>
                    <Shield size={64} color="#ef4444" />
                    <h2 style={{ color: '#f8fafc' }}>접근 권한이 없습니다.</h2>
                    <p style={{ color: '#94a3b8' }}>마스터 관리자 계정으로만 이용 가능합니다.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="management-content">
                <div className="card-header">
                    <h1>접속 및 활동 로그</h1>
                    <button onClick={fetchLogs} className="submit-badge" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#cbd5e1' }}>
                        <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> 새로고침
                    </button>
                </div>

                <div className="glass-card management-card">
                    <div className="settings-header" style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
                            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                            <input 
                                type="text" 
                                placeholder="ID, 계정명, 상세내용 검색..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ paddingLeft: '40px', width: '100%', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#f8fafc', borderRadius: '12px', height: '44px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select 
                                value={filterAction} 
                                onChange={(e) => setFilterAction(e.target.value)}
                                style={{ 
                                    background: 'rgba(30, 41, 59, 0.8)', 
                                    border: '1px solid rgba(255, 255, 255, 0.2)', 
                                    color: '#f8fafc', 
                                    borderRadius: '12px', 
                                    height: '44px', 
                                    padding: '0 12px',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                            >
                                <option value="all" style={{ background: '#1e293b', color: '#f8fafc' }}>전체 활동</option>
                                <option value="login" style={{ background: '#1e293b', color: '#f8fafc' }}>로그인</option>
                                <option value="logout" style={{ background: '#1e293b', color: '#f8fafc' }}>로그아웃</option>
                                <option value="update" style={{ background: '#1e293b', color: '#f8fafc' }}>데이터 수정</option>
                            </select>
                        </div>
                    </div>

                    <div className="management-table-wrapper" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th>일시</th>
                                    <th>ID</th>
                                    <th>계정명</th>
                                    <th>활동유형</th>
                                    <th>상세내용</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '100px 0' }}>
                                            <div className="loading-spinner"></div>
                                            <p style={{ marginTop: '12px', color: '#64748b' }}>로그를 불러오는 중...</p>
                                        </td>
                                    </tr>
                                ) : sortedLogs.length > 0 ? (
                                    sortedLogs.map((log, index) => (
                                        <tr key={index}>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Clock size={14} color="#64748b" />
                                                    {formatTimestamp(log.timestamp)}
                                                </div>
                                            </td>
                                            <td><span style={{ fontWeight: '600' }}>{log.userId}</span></td>
                                            <td>{log.userName}</td>
                                            <td>{getActionBadge(log.action)}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <FileText size={14} color="#64748b" />
                                                    {log.details}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '100px 0', color: '#64748b' }}>
                                            검색 결과가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="glass-card info-card" style={{ marginTop: '20px' }}>
                    <h4>💡 로그 관리 안내</h4>
                    <p>최근 1,000건의 활동 로그가 보관됩니다. admin1, admin2, admin3, admin4 계정의 활동이 실시간으로 기록됩니다.</p>
                </div>
            </div>
        </Layout>
    );
};

export default AccessLogs;
